import React from "react";
import { Document, Page, Text, View } from "@react-pdf/renderer";
import {
    PrepPack,
    PrepPackReport,
    NamedItem,
    ObjectionResponse,
    KeyValueRow,
    isV2Report,
    prepPackFocus,
} from "../../pages/sales/prepPacks/types";
import { PREP_PACK_TOC } from "../../pages/sales/prepPacks/constants";
import { formatMonthYear, registerPdfFonts } from "../../utils";
import { styles } from "./pdfStyles";
import { PrepPackDocPdf } from "./PrepPackDocPdf";

registerPdfFonts();

export const Bullet = ({ children }: { children: React.ReactNode }) => (
    <View style={styles.bullet}>
        <Text style={styles.bulletDot}>•</Text>
        <Text style={styles.bulletText}>{children}</Text>
    </View>
);

export const H2 = ({ children }: { children: React.ReactNode }) => (
    <Text style={styles.h2} minPresenceAhead={520}>
        {children}
    </Text>
);

export const H3 = ({ children }: { children: React.ReactNode }) => (
    <Text style={styles.h3} minPresenceAhead={220}>
        {children}
    </Text>
);

// Only for blocks that fit on one page. A wrap={false} View taller than
// the page cannot be split, so react-pdf overlays its lines on top of each
// other ("Node of type VIEW can't wrap between pages and it's bigger than
// available page height") — never wrap a whole list in it.
export const KeepTogether = ({ children }: { children: React.ReactNode }) => (
    <View wrap={false}>{children}</View>
);

// URLs have no spaces, so react-pdf's textkit cannot wrap them (it only
// breaks at real spaces, and any forced mid-word break appends a hyphen
// glyph; zero-width spaces render as a stray gap). So the URL is split into
// lines here, after the usual separators, at a length that fits the source
// column at 8.5pt, and each line is its own <Text>.
const URL_LINE_CHARS = 88;

export const urlLines = (url: string, max = URL_LINE_CHARS): string[] => {
    const segments = (url || "").match(/[^/?&=_.%#-]*[/?&=_.%#-]?/g) || [];
    const lines: string[] = [];
    let current = "";
    segments.filter(Boolean).forEach((seg) => {
        if (current && current.length + seg.length > max) {
            lines.push(current);
            current = "";
        }
        let rest = seg;
        while (rest.length > max) {
            if (current) {
                lines.push(current);
                current = "";
            }
            lines.push(rest.slice(0, max));
            rest = rest.slice(max);
        }
        current += rest;
    });
    if (current) lines.push(current);
    return lines;
};

const URL_RE = /https?:\/\/\S+/;

// One Source Index entry; wrap={false} keeps a single entry on one page
// while the list as a whole may flow across pages.
export const SourceEntry = ({
    n,
    title,
    url,
}: {
    n: number | string;
    title: string;
    url?: string;
}) => (
    <View style={styles.sourceEntry} wrap={false}>
        <View style={styles.sourceRow}>
            <Text style={styles.sourceNum}>{`[${n}]`}</Text>
            <Text style={styles.sourceTitle}>{title}</Text>
        </View>
        {url
            ? urlLines(url).map((line, i) => (
                  <Text key={i} style={styles.sourceUrl}>
                      {line}
                  </Text>
              ))
            : null}
    </View>
);

// Legacy v1 source lines are free text like "[3] Reuters - Title: https://…";
// split the trailing URL out so it gets the breakable treatment.
export const SourceStrings = ({ items }: { items?: string[] }) => (
    <>
        {(items || []).map((line, i) => {
            const m = (line || "").match(URL_RE);
            const url = m ? m[0] : undefined;
            const text = (url ? line.replace(url, "") : line)
                .replace(/^\s*\[\d+\]\s*/, "")
                .replace(/[\s:]+$/, "");
            const numMatch = (line || "").match(/^\s*\[(\d+)\]/);
            return (
                <SourceEntry
                    key={i}
                    n={numMatch ? numMatch[1] : i + 1}
                    title={text}
                    url={url}
                />
            );
        })}
    </>
);

export const Paragraph = ({ children }: { children?: string | null }) => (
    <Text style={styles.paragraph}>{children || "—"}</Text>
);

const NamedList = ({ items }: { items?: NamedItem[] }) => {
    if (!items || items.length === 0) return <Text style={styles.muted}>—</Text>;
    return (
        <>
            {items.map((it, i) => (
                <View key={i} style={styles.namedItem} wrap={false}>
                    <Text style={styles.namedItemTitle}>{it.title}</Text>
                    <Text style={styles.namedItemBody}>{it.body}</Text>
                </View>
            ))}
        </>
    );
};

export const StringBullets = ({ items }: { items?: string[] }) => {
    if (!items || items.length === 0) return <Text style={styles.muted}>—</Text>;
    return (
        <>
            {items.map((s, i) => (
                <Bullet key={i}>{s}</Bullet>
            ))}
        </>
    );
};

const ObjectionList = ({ items }: { items?: ObjectionResponse[] }) => {
    if (!items || items.length === 0) return <Text style={styles.muted}>—</Text>;
    return (
        <>
            {items.map((o, i) => (
                <View key={i} style={styles.objectionBlock} wrap={false}>
                    <Text style={styles.objectionQuote}>{o.objection}</Text>
                    <Text style={styles.objectionResponseLabel}>Response</Text>
                    <Text style={styles.objectionResponseBody}>{o.response}</Text>
                </View>
            ))}
        </>
    );
};

const AtAGlanceTable = ({ rows }: { rows?: KeyValueRow[] }) => {
    if (!rows || rows.length === 0) return <Text style={styles.muted}>—</Text>;
    return (
        <>
            <View style={styles.tableHead} wrap={false}>
                <Text style={[styles.tableHeadCell, { width: 160 }]}>Attribute</Text>
                <Text style={[styles.tableHeadCell, { flex: 1 }]}>Detail</Text>
            </View>
            {rows.map((r, i) => {
                const isLast = i === rows.length - 1;
                return (
                    <View
                        key={i}
                        style={[
                            styles.tableRow,
                            ...(i % 2 === 1 ? [styles.tableRowAlt] : []),
                            ...(isLast ? [styles.tableRowLast] : []),
                        ]}
                        wrap={false}
                    >
                        <Text style={[styles.tableCell, styles.tableLabelCell]}>{r.label}</Text>
                        <Text style={[styles.tableCell, { flex: 1 }]}>{r.value}</Text>
                    </View>
                );
            })}
        </>
    );
};

export const Footer = ({ code }: { code: string }) => (
    <View style={styles.footer} fixed>
        <Text>Confidential | TDC Internal | {code}</Text>
        <Text render={({ pageNumber }) => `${pageNumber}`} />
        <Text>The Desire Company</Text>
    </View>
);

export const SectionPage = ({
    title,
    code,
    bookmark = true,
    children,
}: {
    title: string;
    code: string;
    bookmark?: boolean;
    children: React.ReactNode;
}) => (
    <Page size="A4" style={styles.page} bookmark={bookmark ? { title } : undefined}>
        <View style={styles.headRule} />
        <Text style={styles.sectionTitle}>{title}</Text>
        <View style={styles.sectionRule} />
        {children}
        <Footer code={code} />
    </Page>
);

export const padCode = (id: number) => `PP-${String(id).padStart(3, "0")}`;

interface Props {
    pack: PrepPack;
    // Internal downloads include QA verdict/warnings diagnostics; the RMN
    // white-label surface hides them from brand users.
    internal?: boolean;
    // Pre-fetched expert avatars (userid -> data URL) for the v2
    // Recommended Experts page; see fetchExpertImages in prepPackFiles.
    expertImages?: Record<number, string>;
}

export const urlHostname = (url: string) => {
    try {
        return new URL(url).hostname.replace(/^www\./, "");
    } catch {
        return url;
    }
};

// Appendix shared by the v1 and v2 renderers: per-URL audit snapshots from
// the deterministic pdp_audit_reports rows (detail payload only).
export const PdpAuditAppendix = ({ pack, code }: { pack: PrepPack; code: string }) => {
    const auditItems = (pack.audits || []).filter((a) => a.report);
    if (auditItems.length === 0) return null;
    return (
        <SectionPage title="Appendix: PDP Audit Snapshot" code={code}>
            <Paragraph>
                Automated PDP audit results for each product URL, rendered
                directly from the audit engine's stored scores. The full
                per-page audits ship alongside this report in the download's
                pdp-audits folder.
            </Paragraph>
            {auditItems.map((a, i) => {
                const audit = a.report!;
                const scores = audit.scores || ({} as any);
                const findings = audit.keyFindings || ({} as any);
                return (
                    <React.Fragment key={a.id}>
                        <KeepTogether>
                            <H2>
                                A.{i + 1} {audit.productName || urlHostname(a.url)}
                            </H2>
                            <Text style={styles.muted}>{a.url}</Text>
                            <Paragraph>
                                {`Overall ${scores.overall}/100 · Human Conversion ${scores.humanConversion} · Agentic Commerce ${scores.agenticCommerce}`}
                            </Paragraph>
                        </KeepTogether>
                        <KeepTogether>
                            <H3>Top issues</H3>
                            <StringBullets items={(findings.opportunities || []).slice(0, 4)} />
                        </KeepTogether>
                        <KeepTogether>
                            <H3>Working well</H3>
                            <StringBullets items={(findings.strengths || []).slice(0, 2)} />
                        </KeepTogether>
                    </React.Fragment>
                );
            })}
        </SectionPage>
    );
};

export function PrepPackPdf({ pack, internal = true, expertImages }: Props) {
    if (isV2Report(pack.report)) {
        return (
            <PrepPackDocPdf
                pack={pack}
                report={pack.report}
                internal={internal}
                expertImages={expertImages}
            />
        );
    }
    return <PrepPackPdfV1 pack={pack} />;
}

function PrepPackPdfV1({ pack }: { pack: PrepPack }) {
    const r: PrepPackReport = (pack.report as PrepPackReport) || {};
    const code = padCode(pack.id);
    const brandName = pack.brand_name || pack.brand?.name || "Brand";
    // Deterministic per-URL audit data (pdp_audit_reports rows) — present
    // only on the detail payload, and only for successfully audited URLs.
    const auditItems = (pack.audits || []).filter((a) => a.report);
    const productList =
        auditItems.map((a) => a.report?.productName).filter(Boolean).join(" — ") ||
        (pack.product_urls || []).map(urlHostname).join(" — ") ||
        (pack.products || []).map((p) => p.title).join(" — ") ||
        pack.category;
    const title = r.title || `${brandName} Brand Intelligence`;
    const subtitle = r.subtitle || `Prospect Briefing: ${prepPackFocus(pack) || productList}`;
    const dateLabel = formatMonthYear(pack.created_at) || formatMonthYear(new Date().toISOString());

    const exec = r.executiveSummary || {};
    const cs = exec.cheatSheet || {};
    const company = r.companyAndBrandProfile || {};
    const dmi = r.decisionMakerIntelligence || {};
    const cat = r.categoryLandscape || {};
    const voc = r.voiceOfCustomer || {};
    const gp = r.meetingGamePlan || {};
    const pkg = r.tdcPackageRecommendation || {};
    const sources = r.sourceIndex || {};

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View style={styles.coverInner}>
                    <Text style={styles.coverTitle}>{title}</Text>
                    <Text style={styles.coverSubtitle}>{subtitle}</Text>
                    <View style={styles.coverDivider} />
                    <Text style={styles.coverMetaPrimary}>Prepared for: The Desire Company Sales Team</Text>
                    <Text style={styles.coverMeta}>Prepared by: TDC Expert and Strategy Team</Text>
                    <Text style={styles.coverMeta}>{dateLabel}</Text>
                </View>
            </Page>

            <SectionPage title="Table of Contents" code={code} bookmark={false}>
                {[
                    ...PREP_PACK_TOC,
                    ...(auditItems.length > 0 ? ["Appendix: PDP Audit Snapshot"] : []),
                ].map((row) => (
                    <View key={row} style={styles.tocRow}>
                        <Text style={styles.tocLabel}>{row}</Text>
                        <View style={styles.tocFill} />
                    </View>
                ))}
            </SectionPage>

            <SectionPage title="1. Executive Summary" code={code}>
                <Paragraph>{exec.overview}</Paragraph>

                <KeepTogether>
                    <H2>1.1 Meeting Cheat Sheet</H2>
                    <H3>Lead line</H3>
                    <Text style={styles.quoteBlock}>{cs.leadLine || "—"}</Text>
                </KeepTogether>

                <KeepTogether>
                    <H3>Three points to lead with</H3>
                    <NamedList items={cs.threePointsToLeadWith} />
                </KeepTogether>

                <KeepTogether>
                    <H3>Top objections and the one-line answer</H3>
                    <ObjectionList items={cs.topObjectionsAndAnswers} />
                </KeepTogether>

                <KeepTogether>
                    <H3>The recommendation</H3>
                    <Paragraph>{cs.recommendation}</Paragraph>
                </KeepTogether>

                <KeepTogether>
                    <H3>The ask</H3>
                    <Paragraph>{cs.ask}</Paragraph>
                </KeepTogether>

                <KeepTogether>
                    <H2>1.2 How to Read This Report</H2>
                    <Paragraph>{exec.howToReadThisPack}</Paragraph>
                </KeepTogether>
            </SectionPage>

            <SectionPage title="2. Company and Brand Profile" code={code}>
                <Paragraph>{company.intro}</Paragraph>

                <KeepTogether>
                    <H2>2.1 At a Glance</H2>
                    <AtAGlanceTable rows={company.atAGlance} />
                </KeepTogether>

                <KeepTogether>
                    <H2>2.2 Flagship Products</H2>
                    <NamedList items={company.flagshipProducts} />
                </KeepTogether>

                <KeepTogether>
                    <H2>2.3 Secondary Product Lines</H2>
                    <NamedList items={company.secondaryProductLines} />
                </KeepTogether>
            </SectionPage>

            <SectionPage title="3. Decision-Maker Intelligence" code={code}>
                <Paragraph>{dmi.orgStructureNote}</Paragraph>

                {dmi.primaryContact ? (
                    <>
                        <KeepTogether>
                            <H2>
                                3.1 Primary Contact{dmi.primaryContact.name ? `: ${dmi.primaryContact.name}` : ""}
                                {dmi.primaryContact.title ? `, ${dmi.primaryContact.title}` : ""}
                            </H2>
                            <H3>Background</H3>
                            <Paragraph>{dmi.primaryContact.background}</Paragraph>
                        </KeepTogether>
                        <KeepTogether>
                            <H3>Priorities</H3>
                            <StringBullets items={dmi.primaryContact.priorities} />
                        </KeepTogether>
                        <KeepTogether>
                            <H3>How to engage</H3>
                            <Paragraph>{dmi.primaryContact.howToEngage}</Paragraph>
                        </KeepTogether>
                    </>
                ) : null}

                {dmi.supportingContacts && dmi.supportingContacts.length > 0 ? (
                    <KeepTogether>
                        <H2>3.2 Supporting Contacts</H2>
                        {dmi.supportingContacts.map((c, i) => (
                            <View key={i} style={styles.namedItem}>
                                <Text style={styles.namedItemTitle}>
                                    {c.name}
                                    {c.title ? `, ${c.title}` : ""}
                                </Text>
                                {c.notes ? <Text style={styles.namedItemBody}>{c.notes}</Text> : null}
                            </View>
                        ))}
                    </KeepTogether>
                ) : null}
            </SectionPage>

            <SectionPage title="4. Category Landscape" code={code}>
                <KeepTogether>
                    <H2>4.1 Market Size & Growth</H2>
                    <Paragraph>{cat.marketSizeAndGrowth}</Paragraph>
                </KeepTogether>

                <KeepTogether>
                    <H2>4.2 Competitive Set</H2>
                    <NamedList items={cat.competitiveSet} />
                </KeepTogether>

                <KeepTogether>
                    <H2>4.3 What Converts in This Category</H2>
                    <StringBullets items={cat.whatConverts} />
                </KeepTogether>

                <KeepTogether>
                    <H2>4.4 Regulatory & Compliance Environment</H2>
                    <Paragraph>{cat.regulatoryEnvironment}</Paragraph>
                </KeepTogether>
            </SectionPage>

            <SectionPage title="5. Voice of Customer" code={code}>
                <KeepTogether>
                    <H2>5.1 Coverage Summary</H2>
                    <Paragraph>{voc.coverageSummary}</Paragraph>
                </KeepTogether>

                <KeepTogether>
                    <H2>5.2 Category-Level Findings</H2>
                    <StringBullets items={voc.categoryFindings} />
                </KeepTogether>

                <KeepTogether>
                    <H2>5.3 Pain Point Library</H2>
                    <NamedList items={voc.painPoints} />
                </KeepTogether>

                <KeepTogether>
                    <H2>5.4 Top Themes</H2>
                    <NamedList items={voc.topThemes} />
                </KeepTogether>

                <KeepTogether>
                    <H2>5.5 Consumer Objections</H2>
                    <NamedList items={voc.consumerObjections} />
                </KeepTogether>

                <KeepTogether>
                    <H2>5.6 Activation Map</H2>
                    <NamedList items={voc.activationMap} />
                </KeepTogether>

                <KeepTogether>
                    <H2>5.7 Customer Language Copy Bank</H2>
                    <StringBullets items={voc.customerLanguageCopyBank} />
                </KeepTogether>
            </SectionPage>

            <SectionPage title="6. Meeting Game Plan" code={code}>
                <KeepTogether>
                    <H2>6.1 Discovery Questions</H2>
                    <StringBullets items={gp.discoveryQuestions} />
                </KeepTogether>

                <KeepTogether>
                    <H2>6.2 Objections and Responses</H2>
                    <ObjectionList items={gp.objectionsAndResponses} />
                </KeepTogether>

                <KeepTogether>
                    <H2>6.3 Proof Points</H2>
                    <StringBullets items={gp.proofPoints} />
                </KeepTogether>
            </SectionPage>

            <SectionPage title="7. TDC Package Recommendation" code={code}>
                <KeepTogether>
                    <H2>7.1 Recommended Package</H2>
                    <Paragraph>{pkg.recommendedPackage}</Paragraph>
                    <Paragraph>{pkg.rationale}</Paragraph>
                </KeepTogether>

                <KeepTogether>
                    <H2>7.2 Asset List</H2>
                    <StringBullets items={pkg.assetList} />
                </KeepTogether>

                <KeepTogether>
                    <H2>7.3 Activation Map by Funnel Stage</H2>
                    <NamedList items={pkg.activationMapByFunnelStage} />
                </KeepTogether>

                <KeepTogether>
                    <H2>7.4 Pilot Scope and Success Metrics</H2>
                    <Paragraph>{pkg.pilotScopeAndSuccessMetrics}</Paragraph>
                </KeepTogether>

                <KeepTogether>
                    <H2>Recommended Experts</H2>
                    <NamedList items={pkg.recommendedExperts} />
                </KeepTogether>
            </SectionPage>

            <SectionPage title="8. Source Index" code={code}>
                <View>
                    <H2>Company, Contact, and Category Sources</H2>
                    <SourceStrings items={sources.companySources} />
                </View>

                <View>
                    <H2>Voice of Customer Sources</H2>
                    <SourceStrings items={sources.voiceOfCustomerSources} />
                </View>
            </SectionPage>

            <PdpAuditAppendix pack={pack} code={code} />
        </Document>
    );
}
